import React, { useState, useEffect } from 'react';
import * as photosApi from '../api/photos';
import BillingPage from './BillingPage';

/* ─── 振り分け実績タブ ─── */
function SortStatsTab() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    photosApi.getStats()
      .then(setStats)
      .catch(() => setError('実績データの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
        <span className="spinner spinner-dark" style={{ width: 24, height: 24 }} />
      </div>
    );
  }

  if (error) return <div className="msg msg-error">{error}</div>;

  const now = new Date();
  const thisMonth = stats?.monthly.find(
    m => m.year === now.getFullYear() && m.month === now.getMonth() + 1
  );

  return (
    <div>
      {/* サマリーカード */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 160,
          background: 'linear-gradient(135deg, #9B5FE3, #7048D4)',
          borderRadius: 20, padding: '24px 28px', color: '#fff',
          boxShadow: '0 6px 20px rgba(112,72,212,0.25)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.8, marginBottom: 8, letterSpacing: '0.06em' }}>
            累計振り分け枚数
          </div>
          <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1 }}>
            {(stats?.total_sorted ?? 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>枚</div>
        </div>

        <div style={{
          flex: 1, minWidth: 160,
          background: '#fff', borderRadius: 20, padding: '24px 28px',
          border: '1px solid var(--border)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.06em' }}>
            今月の振り分け枚数
          </div>
          <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1, color: '#FF6B9D' }}>
            {(thisMonth?.sorted_count ?? 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {now.getFullYear()}年{now.getMonth() + 1}月
          </div>
        </div>
      </div>

      {/* 月別一覧テーブル */}
      {!stats?.monthly.length ? (
        <div className="empty-state" style={{ paddingTop: 32 }}>
          <div className="empty-icon">📊</div>
          <div className="empty-text">まだ振り分け実績がありません</div>
        </div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: 16,
          border: '1px solid var(--border)',
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            fontWeight: 700, fontSize: 13, color: 'var(--text-muted)',
          }}>
            月別 振り分け実績
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', textAlign: 'left', background: '#fafafa' }}>
                <th style={{ padding: '10px 20px', fontWeight: 600 }}>年月</th>
                <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: 'right' }}>振り分け枚数</th>
              </tr>
            </thead>
            <tbody>
              {[...stats.monthly].reverse().map((m, i) => {
                const isCurrentMonth = m.year === now.getFullYear() && m.month === now.getMonth() + 1;
                return (
                  <tr key={i} style={{
                    borderBottom: '1px solid var(--border-light)',
                    background: isCurrentMonth ? '#fdf5ff' : 'transparent',
                  }}>
                    <td style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isCurrentMonth && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                          background: '#9B5FE3', color: '#fff',
                        }}>今月</span>
                      )}
                      <span style={{ fontWeight: isCurrentMonth ? 700 : 400 }}>
                        {m.year}年{String(m.month).padStart(2, '0')}月
                      </span>
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 700, fontSize: 15 }}>
                      {m.sorted_count.toLocaleString()}
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>枚</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── メインページ ─── */
const TABS = [
  { id: 'sort',    label: '振り分け実績', icon: '📊' },
  { id: 'billing', label: 'サブスクリプション', icon: '💳' },
  // 将来追加: { id: 'receipts', label: '領収書', icon: '🧾' },
];

export default function UsagePage() {
  const [tab, setTab] = useState('sort');

  return (
    <div className="page">
      <div className="page-header animate-in">
        <h1 className="page-title">利用実績</h1>
        <p className="page-subtitle">振り分け実績・サブスクリプションの管理</p>
      </div>

      <div className="page-body">
        {/* タブバー */}
        <div className="tab-bar animate-in" style={{ marginBottom: 24 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              className={`tab-btn${tab === t.id ? ' active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="animate-in-delay-1">
          {tab === 'sort'    && <SortStatsTab />}
          {tab === 'billing' && <BillingPage embedded />}
        </div>
      </div>
    </div>
  );
}
